// ============================================================================
// Shared Component: JobFormFields
// ============================================================================
// Renders all job form fields in a consistent layout.
// Used by both JobDetailModal and MissingInvalidData/JobImportForm.
//
// IMPORT_FIELDS: If you are adding a new column, add it here.
// This is the single source of truth for form field layout.

import { useContext, useState } from "react";
import {
	Button,
	Dialog,
	DialogContent,
	DialogTitle,
	IconButton,
	Menu,
	MenuItem,
	Stack,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Map as MapIcon, Close as CloseIcon, Download as DownloadIcon } from "@mui/icons-material";
import type { FeatureCollection } from "geojson";
import { GeoData } from "@rogoag/shared-ts";
import { FileDownloader } from "../../../utilities/downloadFile";
import { toast } from "react-toastify";
import { PortalTooltip } from "../../helpers";
import { UserContext } from "../../../hooks/UserContext";
import {
	DOWNLOAD_FORMAT_OPTIONS,
	GeoJSONDownloadFormat,
} from "../ImportTable/ImportTable/columns/standard-column-types/geoJSONFileColumn/geoJSONFileColumn.types";
import type { Branch, Job } from "../../../models";
import type { ImportTableColumn } from "../../../utilities/types";
import { getJobPriorities, JobsPriority } from "../../../utilities/types";
import type { ColumnContext } from "../ImportTable/ImportTable/columns/standard-column-types/ColumnContext";
import type { DropdownOptions } from "./useDropdownOptions";
import type { BoundsPadding, EditableJsonKeys } from "../../helpers/JobMap/JobMap.types";
import JobMap from "../../helpers/JobMap";

// Mobile column components
import { TextColumnMobile } from "../ImportTable/ImportTable/columns/standard-column-types/textColumn/textColumn.mobile";
import { SelectColumnMobile } from "../ImportTable/ImportTable/columns/standard-column-types/selectColumn/selectColumn.mobile";
import { CheckboxColumnMobile } from "../ImportTable/ImportTable/columns/standard-column-types/checkboxColumn/checkboxColumn.mobile";
import { DateColumnMobile } from "../ImportTable/ImportTable/columns/standard-column-types/dateColumn/dateColumn.mobile";
import { GeoJSONFileColumnMobile } from "../ImportTable/ImportTable/columns/standard-column-types/geoJSONFileColumn/geoJSONFileColumn.mobile";
import { DealColumnMobile } from "../ImportTable/ImportTable/columns/Deal.column/Deal.column.mobile";
import { SalespersonColumnMobile } from "../ImportTable/ImportTable/columns/Salesperson.column/Salesperson.column.mobile";
import { SalespersonPhoneColumnMobile } from "../ImportTable/ImportTable/columns/SalespersonPhone.column/SalespersonPhone.column.mobile";
import { GrowerColumnMobile } from "../ImportTable/ImportTable/columns/Grower.column/Grower.column.mobile";
import { LastCropColumnMobile } from "../ImportTable/ImportTable/columns/LastCrop.column/LastCrop.column.mobile";
import { NextCropColumnMobile } from "../ImportTable/ImportTable/columns/NextCrop.column/NextCrop.column.mobile";
import { FieldNotesColumnMobile } from "../ImportTable/ImportTable/columns/FieldNotes.column/FieldNotes.column.mobile";

// ============================================================================
// Types
// ============================================================================

export interface JobFormFieldsProps {
	/** Current job being edited */
	job: Job;
	/** Column context for mobile columns */
	columnContext: ColumnContext;
	/** Dropdown options for select fields */
	dropdownOptions: DropdownOptions;
	/** Available branches for the branch dropdown */
	branches?: Branch[];
	/** Function to check if a field is visible */
	isVisible: (columnId: ImportTableColumn) => boolean;
	/** Whether this is bulk edit mode (disables required indicators, shows mixed value placeholders) */
	isBulkEdit?: boolean;
	/** Function to check if a field has mixed values across selected jobs (only used in bulk edit) */
	hasMixedValues?: (field: keyof Job) => boolean;
	/** Whether secondary fields are shown (undefined = show all, no toggle) */
	showMoreFields?: boolean;
	/** Callback to toggle show more fields */
	onToggleShowMore?: () => void;
	/** Callback for geometry edits on the map (enables edit toolbar when provided) */
	onGeometryEdit?: (jobId: string, jsonKey: EditableJsonKeys, updated: FeatureCollection) => void;
	/** Whether the user is a super user (enables geometry editing on the map) */
	isSuperUser?: boolean;
}

// ============================================================================
// Constants
// ============================================================================

const MAP_BOUNDS_PADDING: BoundsPadding = { top: 20, right: 20, bottom: 20, left: 20 };

/** Column IDs for secondary-only sections (no primary fields) */
const SECONDARY_ONLY_SECTION_COLUMNS: ImportTableColumn[][] = [
	["boundaryGeoJSON", "pointsGeoJSON", "zonesGeoJSON"], // Attachments
	["fieldNotes"], // Field Notes
	["isReadyToSample", "dateFieldReadyEstimated"], // Order
	["collectBoundary", "fieldPriority"], // Priority
	["submissionNotes"], // Submission Notes
	["density", "depth", "frequencyOfMicros", "labName", "testPackage"], // Deal row
	["deal"], // Deal config
	["labInstructions", "microsList"], // Lab Info
	["branch"], // Branch
	["salesperson", "salespersonMobilePhone"], // Salesperson
	["growerPhoneNumber"], // Grower Phone
	["submitterName", "submitterEmail"], // Submitter
	["externalId", "externalFarmId", "externalLabId"], // External IDs
	["isTestJob"], // Test Job
];

// ============================================================================
// Component
// ============================================================================

/**
 * Renders all job form fields in a consistent layout.
 *
 * This component is the single source of truth for form field ordering and layout.
 * Both JobDetailModal and MissingInvalidData/JobImportForm use this component.
 *
 * In bulk edit mode (isBulkEdit=true):
 * - Required indicators are hidden
 * - Fields with mixed values show placeholder text
 *
 * When showMoreFields is provided:
 * - false: Only primary field sections are shown (grower/farm/field, crops, sync codes, fieldId)
 * - true: All fields are shown
 * - undefined: All fields shown, no toggle button (backward compatible)
 */
export function JobFormFields({
	job,
	columnContext,
	dropdownOptions,
	branches = [],
	isVisible,
	isBulkEdit = false,
	hasMixedValues,
	showMoreFields,
	onToggleShowMore,
	onGeometryEdit,
	isSuperUser,
}: JobFormFieldsProps) {
	const { gridSizes, depths, labPackages, micros } = dropdownOptions;
	const [bigMapOpen, setBigMapOpen] = useState(false);
	const [isDownloadingAll, setIsDownloadingAll] = useState(false);
	const [downloadAllMenuAnchorEl, setDownloadAllMenuAnchorEl] = useState<null | HTMLElement>(null);
	const showMap = !!onGeometryEdit;
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
	const userContext = useContext(UserContext);
	const isSuperuser = isSuperUser ?? userContext?.isAdmin ?? false;

	const geoCollections = [job.files?.boundaryGeoJSON, job.files?.pointsGeoJSON, job.files?.zonesGeoJSON].filter(
		(fc): fc is FeatureCollection => !!fc,
	);

	const hasGeoFiles = geoCollections.length > 0;

	const handleDownloadAllGeoFiles = async (format: GeoJSONDownloadFormat = "shapefile") => {
		if (!hasGeoFiles) return;
		setDownloadAllMenuAnchorEl(null);
		setIsDownloadingAll(true);
		try {
			const merged = GeoData.merge(geoCollections);
			const baseName =
				[job.importFarmName || job.farmName, job.importFieldName || job.fieldName].filter(Boolean).join("_") || job.id;
			const filename = `${baseName}_all_geo`;
			let file: File;
			switch (format) {
				case "kml":
					file = await GeoData.toKML_File(merged, "GeoJSON", filename);
					await FileDownloader.downloadFile(file, `${filename}.kml`);
					break;
				case "kmz":
					file = await GeoData.toKMZ_File(merged, "GeoJSON", filename);
					await FileDownloader.downloadFile(file, `${filename}.kmz`);
					break;
				case "geojson":
					file = await GeoData.toGeoJSON_File(merged, "GeoJSON", filename);
					await FileDownloader.downloadFile(file, `${filename}.json`);
					break;
				case "shapefile":
				default:
					file = await GeoData.toSHP_File(merged, "GeoJSON", filename);
					await FileDownloader.downloadFile(file, `${filename}.zip`);
					break;
			}
		} catch (error) {
			const message = error instanceof Error ? error.message : "Download failed";
			toast.error(message);
		} finally {
			setIsDownloadingAll(false);
		}
	};

	// Whether the show more/less feature is active
	const hasShowMoreToggle = showMoreFields !== undefined;
	// Whether secondary fields should be rendered
	const showSecondary = !hasShowMoreToggle || showMoreFields;

	// Count visible secondary fields for the button label
	const secondaryFieldCount = hasShowMoreToggle
		? SECONDARY_ONLY_SECTION_COLUMNS.flat().filter((col) => isVisible(col)).length
		: 0;

	// Helper to get placeholder for mixed values
	const getMixedPlaceholder = (field: keyof Job): string | undefined => {
		if (isBulkEdit && hasMixedValues?.(field)) {
			return "Multiple values";
		}
		return undefined;
	};

	// In bulk edit mode, don't mark fields as required
	const isRequired = (defaultRequired: boolean): boolean => {
		return isBulkEdit ? false : defaultRequired;
	};

	return (
		<Stack spacing={2}>
			{/* Basic Information - Grower, Farm, Field side-by-side (PRIMARY) */}
			<Grid container spacing={2}>
				{isVisible("grower") && (
					<Grid size={{ xs: 12, sm: "grow" }}>
						<GrowerColumnMobile job={job} columnContext={columnContext} required={isRequired(true)} />
					</Grid>
				)}
				{isVisible("farm") && (
					<Grid size={{ xs: 12, sm: "grow" }}>
						<TextColumnMobile
							job={job}
							columnContext={columnContext}
							fieldName="importFarmName"
							columnId="farm"
							placeholder={getMixedPlaceholder("importFarmName")}
						/>
					</Grid>
				)}
				{isVisible("field") && (
					<Grid size={{ xs: 12, sm: "grow" }}>
						<TextColumnMobile
							job={job}
							columnContext={columnContext}
							fieldName="importFieldName"
							columnId="field"
							required={isRequired(true)}
							placeholder={getMixedPlaceholder("importFieldName")}
						/>
					</Grid>
				)}
			</Grid>

			{/* Field ID - full width (PRIMARY) */}
			{isVisible("fieldId") && (
				<TextColumnMobile job={job} columnContext={columnContext} fieldName="fieldId" columnId="fieldId" />
			)}

			{/* Last Crop, Next Crop, and Yield Goal side-by-side (MIXED - contains primary lastCrop/nextCrop) */}
			<Grid container spacing={2}>
				{isVisible("lastCrop") && (
					<Grid size={{ xs: 12, sm: "grow" }}>
						<LastCropColumnMobile job={job} columnContext={columnContext} />
					</Grid>
				)}
				{isVisible("nextCrop") && (
					<Grid size={{ xs: 12, sm: "grow" }}>
						<NextCropColumnMobile job={job} columnContext={columnContext} />
					</Grid>
				)}
				{isVisible("yieldGoal") && (
					<Grid size={{ xs: 12, sm: "grow" }}>
						<TextColumnMobile job={job} columnContext={columnContext} fieldName="yieldGoal" columnId="yieldGoal" />
					</Grid>
				)}
			</Grid>

			{/* Sync Codes - Event ID and Lab Submission Code side-by-side (MIXED - contains primary eventId) */}
			<Grid container spacing={2}>
				{isVisible("eventId") && (
					<Grid size={{ xs: 12, sm: "grow" }}>
						<TextColumnMobile job={job} columnContext={columnContext} fieldName="eventId" columnId="eventId" />
					</Grid>
				)}
				{isVisible("labSubmissionCode") && (
					<Grid size={{ xs: 12, sm: "grow" }}>
						<TextColumnMobile
							job={job}
							columnContext={columnContext}
							fieldName="labSubmissionCode"
							columnId="labSubmissionCode"
						/>
					</Grid>
				)}
			</Grid>

			{/* Show More / Show Less toggle button */}
			{hasShowMoreToggle && secondaryFieldCount > 0 && (
				<Button
					onClick={onToggleShowMore}
					size="small"
					startIcon={<ExpandMoreIcon sx={{ transform: showMoreFields ? "rotate(180deg)" : undefined }} />}
					sx={{ alignSelf: "flex-start" }}
				>
					{showMoreFields ? "Show Less" : `Show ${secondaryFieldCount} More`}
				</Button>
			)}

			{/* ============ SECONDARY FIELDS (hidden when showMoreFields === false) ============ */}
			{showSecondary && (
				<>
					{/* Attachments + Map side-by-side */}
					<Grid container spacing={2}>
						{showMap ? (
							<>
								{/* File columns - stacked vertically on left */}
								<Grid size={{ xs: 12, sm: 4 }}>
									<Stack spacing={2}>
										<Stack direction="row" spacing={1} alignItems="center">
											{isSuperuser && showMap && (
												<Button
													variant="contained"
													size="small"
													startIcon={<MapIcon />}
													onClick={() => setBigMapOpen(true)}
													sx={{ flex: 1 }}
												>
													Map Tools
												</Button>
											)}
											{isSuperuser && showMap && hasGeoFiles && (
												<PortalTooltip title="Download all (merged)">
													<span>
														<IconButton
															size="small"
															onClick={(e) => setDownloadAllMenuAnchorEl(e.currentTarget)}
															disabled={isDownloadingAll}
														>
															<DownloadIcon fontSize="small" />
														</IconButton>
													</span>
												</PortalTooltip>
											)}
										</Stack>
										{isVisible("boundaryGeoJSON") && (
											<GeoJSONFileColumnMobile
												job={job}
												columnContext={columnContext}
												optionsKey="boundaryOptions"
												geoJSONKey="boundaryGeoJSON"
												columnId="boundaryGeoJSON"
											/>
										)}
										{isVisible("pointsGeoJSON") && (
											<GeoJSONFileColumnMobile
												job={job}
												columnContext={columnContext}
												optionsKey="pointsOptions"
												geoJSONKey="pointsGeoJSON"
												columnId="pointsGeoJSON"
											/>
										)}
										{isVisible("zonesGeoJSON") && (
											<GeoJSONFileColumnMobile
												job={job}
												columnContext={columnContext}
												optionsKey="zonesOptions"
												geoJSONKey="zonesGeoJSON"
												columnId="zonesGeoJSON"
											/>
										)}
										{isVisible("fieldNotes") && <FieldNotesColumnMobile job={job} columnContext={columnContext} />}
									</Stack>
								</Grid>
								{/* Map - right, stretch to match file columns */}
								<Grid size={{ xs: 12, sm: 8 }} sx={{ minHeight: 250 }}>
									<JobMap
										job={job}
										height="100%"
										colorMode="layer-type"
										showEmptyState
										disableDefaultUI
										zoomControl
										boundsPadding={MAP_BOUNDS_PADDING}
									/>
								</Grid>
							</>
						) : (
							<>
								{/* File columns - horizontal when no map */}
								{isSuperuser && showMap && hasGeoFiles && (
									<PortalTooltip title="Download all (merged)">
										<span>
											<IconButton
												size="small"
												onClick={(e) => setDownloadAllMenuAnchorEl(e.currentTarget)}
												disabled={isDownloadingAll}
											>
												<DownloadIcon fontSize="small" />
											</IconButton>
										</span>
									</PortalTooltip>
								)}
								{isVisible("boundaryGeoJSON") && (
									<Grid size={{ xs: 12, sm: "grow" }}>
										<GeoJSONFileColumnMobile
											job={job}
											columnContext={columnContext}
											optionsKey="boundaryOptions"
											geoJSONKey="boundaryGeoJSON"
											columnId="boundaryGeoJSON"
										/>
									</Grid>
								)}
								{isVisible("pointsGeoJSON") && (
									<Grid size={{ xs: 12, sm: "grow" }}>
										<GeoJSONFileColumnMobile
											job={job}
											columnContext={columnContext}
											optionsKey="pointsOptions"
											geoJSONKey="pointsGeoJSON"
											columnId="pointsGeoJSON"
										/>
									</Grid>
								)}
								{isVisible("zonesGeoJSON") && (
									<Grid size={{ xs: 12, sm: "grow" }}>
										<GeoJSONFileColumnMobile
											job={job}
											columnContext={columnContext}
											optionsKey="zonesOptions"
											geoJSONKey="zonesGeoJSON"
											columnId="zonesGeoJSON"
										/>
									</Grid>
								)}
							</>
						)}
					</Grid>
					{!showMap && isVisible("fieldNotes") && <FieldNotesColumnMobile job={job} columnContext={columnContext} />}
					{/* Big Map Dialog */}
					{showMap && (
						<Dialog
							open={bigMapOpen}
							onClose={() => setBigMapOpen(false)}
							maxWidth={false}
							fullScreen={isMobile}
							slotProps={{
								paper: {
									sx: isMobile ? undefined : { width: "90vw", height: "90vh", maxWidth: "none" },
								},
							}}
						>
							<DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 1 }}>
								Map
								<IconButton onClick={() => setBigMapOpen(false)} size="small">
									<CloseIcon />
								</IconButton>
							</DialogTitle>
							<DialogContent sx={{ p: 0, overflow: "hidden" }}>
								<JobMap
									job={job}
									height="100%"
									colorMode="layer-type"
									showEmptyState
									disableDefaultUI
									zoomControl
									boundsPadding={MAP_BOUNDS_PADDING}
									onGeometryEdit={isSuperuser ? onGeometryEdit : undefined}
								/>
							</DialogContent>
						</Dialog>
					)}

					{/* Download format menu for superusers */}
					<Menu
						anchorEl={downloadAllMenuAnchorEl}
						open={Boolean(downloadAllMenuAnchorEl)}
						onClose={() => setDownloadAllMenuAnchorEl(null)}
					>
						{DOWNLOAD_FORMAT_OPTIONS.map((opt) => (
							<MenuItem key={opt.value} onClick={() => handleDownloadAllGeoFiles(opt.value)}>
								{opt.label}
							</MenuItem>
						))}
					</Menu>

					{/* Order - Ready to Sample and Est Field Ready Date side-by-side */}
					<Grid container spacing={2}>
						{isVisible("isReadyToSample") && (
							<Grid size={{ xs: 12, sm: "grow" }}>
								<CheckboxColumnMobile
									job={job}
									columnContext={columnContext}
									fieldName="isReadyToSample"
									columnId="isReadyToSample"
								/>
							</Grid>
						)}
						{isVisible("dateFieldReadyEstimated") && (
							<Grid size={{ xs: 12, sm: "grow" }}>
								<DateColumnMobile
									job={job}
									columnContext={columnContext}
									fieldName="dateFieldReadyEstimated"
									columnId="dateFieldReadyEstimated"
								/>
							</Grid>
						)}
					</Grid>

					{/* Field Priority and Collect Boundary */}
					<Grid container spacing={2}>
						{isVisible("collectBoundary") && (
							<Grid size={{ xs: 12, sm: "grow" }}>
								<CheckboxColumnMobile
									job={job}
									columnContext={columnContext}
									fieldName="collectBoundary"
									columnId="collectBoundary"
								/>
							</Grid>
						)}
						{isVisible("fieldPriority") && (
							<Grid size={{ xs: 12, sm: "grow" }}>
								<SelectColumnMobile<string>
									job={job}
									columnContext={columnContext}
									fieldName="fieldPriority"
									columnId="fieldPriority"
									options={[...getJobPriorities()]}
									freeSolo={false}
									getValue={(j) => j.fieldPriority || "Regular Turn (default)"}
									setValue={(j, value) => {
										j.fieldPriority = (value || "Regular Turn (default)") as JobsPriority;
									}}
								/>
							</Grid>
						)}
					</Grid>

					{/* Submission Notes - full width */}
					{isVisible("submissionNotes") && (
						<TextColumnMobile
							job={job}
							columnContext={columnContext}
							fieldName="submissionNotes"
							columnId="submissionNotes"
							multiline
							rows={3}
						/>
					)}

					{/* Deal - Density, Depth, Freq of Micros, Lab Name, Test Package side-by-side */}
					<Grid container spacing={2}>
						{isVisible("density") && (
							<Grid size={{ xs: 12, sm: "grow" }}>
								<SelectColumnMobile
									job={job}
									columnContext={columnContext}
									fieldName="importDensity"
									columnId="density"
									options={gridSizes}
									isNumber
									required={isRequired(true)}
								/>
							</Grid>
						)}
						{isVisible("depth") && (
							<Grid size={{ xs: 12, sm: "grow" }}>
								<SelectColumnMobile
									job={job}
									columnContext={columnContext}
									fieldName="importDepth"
									columnId="depth"
									options={depths}
									isNumber
									required={isRequired(true)}
								/>
							</Grid>
						)}
						{isVisible("frequencyOfMicros") && (
							<Grid size={{ xs: 12, sm: "grow" }}>
								<SelectColumnMobile
									job={job}
									columnContext={columnContext}
									fieldName="externalFrequencyOfMicros"
									columnId="frequencyOfMicros"
									options={micros}
									isNumber
								/>
							</Grid>
						)}
						{isVisible("labName") && (
							<Grid size={{ xs: 12, sm: "grow" }}>
								<TextColumnMobile
									job={job}
									columnContext={columnContext}
									fieldName="importLabName"
									columnId="labName"
									placeholder={getMixedPlaceholder("importLabName")}
								/>
							</Grid>
						)}
						{isVisible("testPackage") && (
							<Grid size={{ xs: 12, sm: "grow" }}>
								<SelectColumnMobile
									job={job}
									columnContext={columnContext}
									fieldName="externalTestPackageId"
									columnId="testPackage"
									options={labPackages}
								/>
							</Grid>
						)}
					</Grid>

					{/* Deal Configuration - full width */}
					{isVisible("deal") && (
						<DealColumnMobile job={job} columnContext={columnContext} required={isRequired(true)} />
					)}

					{/* Lab Info */}
					{isVisible("labInstructions") && (
						<TextColumnMobile
							job={job}
							columnContext={columnContext}
							fieldName="labInstructions"
							columnId="labInstructions"
							multiline
							rows={2}
						/>
					)}
					{isVisible("microsList") && (
						<TextColumnMobile
							job={job}
							columnContext={columnContext}
							fieldName="microsList"
							columnId="microsList"
							disabled={!job?.files?.pointsGeoJSON?.features?.length}
						/>
					)}

					{/* Contacts - Branch full width */}
					{isVisible("branch") && (
						<SelectColumnMobile<Branch>
							job={job}
							columnContext={columnContext}
							fieldName="branchIds"
							columnId="branch"
							options={branches}
							freeSolo={false}
							getOptionLabel={(branch) => branch.name}
							isOptionEqualToValue={(option, value) => option.id === value.id}
							getValue={(j) => branches.find((branch) => j.branchIds?.includes(branch.id)) || null}
							setValue={(j, branch) => {
								j.branchIds = branch ? [branch.id] : [];
							}}
						/>
					)}

					{/* Salesperson and Salesperson Phone side-by-side */}
					<Grid container spacing={2}>
						{isVisible("salesperson") && (
							<Grid size={{ xs: 12, sm: "grow" }}>
								<SalespersonColumnMobile job={job} columnContext={columnContext} />
							</Grid>
						)}
						{isVisible("salespersonMobilePhone") && (
							<Grid size={{ xs: 12, sm: "grow" }}>
								<SalespersonPhoneColumnMobile job={job} columnContext={columnContext} />
							</Grid>
						)}
					</Grid>

					{/* Grower Phone - full width */}
					{isVisible("growerPhoneNumber") && (
						<TextColumnMobile
							job={job}
							columnContext={columnContext}
							fieldName="growerPhoneNumber"
							columnId="growerPhoneNumber"
							inputType="tel"
						/>
					)}

					{/* Submitter Name and Email side-by-side */}
					<Grid container spacing={2}>
						{isVisible("submitterName") && (
							<Grid size={{ xs: 12, sm: "grow" }}>
								<TextColumnMobile
									job={job}
									columnContext={columnContext}
									fieldName="submitterName"
									columnId="submitterName"
								/>
							</Grid>
						)}
						{isVisible("submitterEmail") && (
							<Grid size={{ xs: 12, sm: "grow" }}>
								<TextColumnMobile
									job={job}
									columnContext={columnContext}
									fieldName="submitterEmail"
									columnId="submitterEmail"
									inputType="email"
								/>
							</Grid>
						)}
					</Grid>

					{/* External ID, External Farm ID, External Lab ID side-by-side */}
					<Grid container spacing={2}>
						{isVisible("externalId") && (
							<Grid size={{ xs: 12, sm: "grow" }}>
								<TextColumnMobile
									job={job}
									columnContext={columnContext}
									fieldName="externalId"
									columnId="externalId"
								/>
							</Grid>
						)}
						{isVisible("externalFarmId") && (
							<Grid size={{ xs: 12, sm: "grow" }}>
								<TextColumnMobile
									job={job}
									columnContext={columnContext}
									fieldName="externalFarmId"
									columnId="externalFarmId"
								/>
							</Grid>
						)}
						{isVisible("externalLabId") && (
							<Grid size={{ xs: 12, sm: "grow" }}>
								<TextColumnMobile
									job={job}
									columnContext={columnContext}
									fieldName="externalLabId"
									columnId="externalLabId"
								/>
							</Grid>
						)}
					</Grid>

					{/* Misc */}
					{isVisible("isTestJob") && (
						<CheckboxColumnMobile job={job} columnContext={columnContext} fieldName="isTestJob" columnId="isTestJob" />
					)}
				</>
			)}
		</Stack>
	);
}
